import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Upload, Save, Building2, Image, Globe, Phone, Mail, MapPin, Clock, Crown, Check, Sparkles, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { compressImage } from '../../utils/imageUtils';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../config/firebase';
import { useBusiness } from '../../contexts/BusinessContext';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Loading } from '../../components/ui/Loading';
import { useSystemConfig } from '../../hooks/useSystemConfig';

interface BusinessConfig {
  name: string;
  description: string;
  logo: string;
  coverImage: string;
  phone: string;
  email: string;
  address: string;
  website: string;
  socialMedia: {
    instagram: string;
    facebook: string;
    twitter: string;
  };
  openingHours: string;
  cancellationPolicy: {
    hoursBeforeClass: number; // Horas antes de la clase para cancelar sin penalización
    maxReservationsPerDay: number; // Máximo de reservas por usuario por día
  };
  requireUserApproval: boolean; // Si los nuevos usuarios necesitan aprobación del admin
}

interface SubscriptionInfo {
  plan: 'free' | 'basic' | 'pro';
  status: string;
  currentPeriodEnd?: number;
  cancelAtPeriodEnd?: boolean;
}


const BusinessConfigPage: React.FC = () => {
  const { businessId } = useBusiness();
  const { config: systemConfig } = useSystemConfig(); // Configuración global del sistema
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionInfo>({ plan: 'free', status: 'active' });
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [config, setConfig] = useState<BusinessConfig>({
    name: '',
    description: '',
    logo: '',
    coverImage: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    socialMedia: {
      instagram: '',
      facebook: '',
      twitter: '',
    },
    openingHours: '',
    cancellationPolicy: {
      hoursBeforeClass: 2, // Por defecto 2 horas
      maxReservationsPerDay: 1, // Por defecto 1 clase por día
    },
    requireUserApproval: false, // Por defecto no requiere aprobación
  });

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadConfig = async () => {
      if (!businessId) {
        setLoading(false);
        return;
      }

      try {
        const businessDoc = await getDoc(doc(db, 'businesses', businessId));
        if (businessDoc.exists()) {
          const data = businessDoc.data();
          setConfig({
            name: data.name || '',
            description: data.description || '',
            logo: data.logo || '',
            coverImage: data.coverImage || '',
            phone: data.phone || '',
            email: data.adminEmail || data.email || '',
            address: data.address || '',
            website: data.website || '',
            socialMedia: {
              instagram: data.socialMedia?.instagram || '',
              facebook: data.socialMedia?.facebook || '',
              twitter: data.socialMedia?.twitter || '',
            },
            openingHours: data.openingHours || '',
            cancellationPolicy: {
              hoursBeforeClass: data.cancellationPolicy?.hoursBeforeClass ?? 2,
              maxReservationsPerDay: data.cancellationPolicy?.maxReservationsPerDay ?? 1,
            },
            requireUserApproval: data.requireUserApproval ?? false,
          });
          setLogoPreview(data.logo || null);
          setCoverPreview(data.coverImage || null);
        }
      } catch (error) {
        console.error('Error loading config:', error);
        toast.error('Error al cargar la configuración');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [businessId]);

  // Cargar estado de suscripción
  useEffect(() => {
    const loadSubscription = async () => {
      if (!businessId) return;

      setLoadingSubscription(true);
      try {
        const getSubscriptionStatus = httpsCallable(functions, 'getSubscriptionStatus');
        const result = await getSubscriptionStatus({ businessId });
        const data = result.data as SubscriptionInfo;
        setSubscription(data);
      } catch (error) {
        console.error('Error loading subscription:', error);
        // Si falla, asumimos plan gratuito
        setSubscription({ plan: 'free', status: 'active' });
      } finally {
        setLoadingSubscription(false);
      }
    };

    loadSubscription();
  }, [businessId]);

  // Manejar checkout de suscripción
  const handleSubscribe = async (planId: 'basic' | 'pro') => {
    if (!businessId) return;

    setProcessingCheckout(true);
    try {
      const createSubscriptionCheckout = httpsCallable(functions, 'createSubscriptionCheckout');
      const result = await createSubscriptionCheckout({
        businessId,
        planId,
        successUrl: `${window.location.origin}/admin/config?subscription=success`,
        cancelUrl: `${window.location.origin}/admin/config?subscription=canceled`,
      });

      const data = result.data as { success: boolean; url: string };

      if (data.success && data.url) {
        // Redirigir a Stripe Checkout en nueva pestaña
        window.open(data.url, '_blank');
      } else {
        toast.error('Error al crear la sesión de pago');
      }
    } catch (error: any) {
      console.error('Error creating checkout:', error);
      toast.error(error.message || 'Error al procesar el pago');
    } finally {
      setProcessingCheckout(false);
    }
  };

  // Abrir portal de cliente de Stripe
  const handleManageSubscription = async () => {
    if (!businessId) return;

    setProcessingCheckout(true);
    try {
      const createCustomerPortal = httpsCallable(functions, 'createCustomerPortal');
      const result = await createCustomerPortal({
        businessId,
        returnUrl: `${window.location.origin}/admin/config`,
      });

      const data = result.data as { success: boolean; url: string };

      if (data.success && data.url) {
        window.open(data.url, '_blank');
      } else {
        toast.error('Error al abrir el portal de gestión');
      }
    } catch (error: any) {
      console.error('Error opening portal:', error);
      toast.error(error.message || 'Error al abrir el portal');
    } finally {
      setProcessingCheckout(false);
    }
  };

  // Detectar parámetros de URL para mostrar mensajes de éxito/error
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subscriptionStatus = params.get('subscription');

    if (subscriptionStatus === 'success') {
      toast.success('¡Suscripción activada correctamente! 🎉');
      // Limpiar URL
      window.history.replaceState({}, '', window.location.pathname);
      // Recargar estado de suscripción
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } else if (subscriptionStatus === 'canceled') {
      toast.error('Pago cancelado');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleChange = (field: string, value: string) => {
    if (field.startsWith('socialMedia.')) {
      const socialField = field.split('.')[1];
      setConfig(prev => ({
        ...prev,
        socialMedia: {
          ...prev.socialMedia,
          [socialField]: value,
        },
      }));
    } else {
      setConfig(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
    type: 'logo' | 'cover'
  ) => {
    const file = e.target.files?.[0];
    if (!file || !businessId) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingCover;
    const setPreview = type === 'logo' ? setLogoPreview : setCoverPreview;
    const configField = type === 'logo' ? 'logo' : 'coverImage';

    setUploading(true);

    try {
      // Preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);

      // Upload
      const compressedFile = await compressImage(file);
      const fileName = `businesses/${businessId}/${type}_${Date.now()}_${compressedFile.name}`;
      const storageRef = ref(storage, fileName);
      await uploadBytes(storageRef, compressedFile);
      const downloadUrl = await getDownloadURL(storageRef);

      setConfig(prev => ({ ...prev, [configField]: downloadUrl }));
      toast.success('Imagen subida correctamente');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!businessId) return;

    setSaving(true);
    try {
      await updateDoc(doc(db, 'businesses', businessId), {
        name: config.name,
        description: config.description,
        logo: config.logo,
        coverImage: config.coverImage,
        phone: config.phone,
        email: config.email,
        address: config.address,
        website: config.website,
        socialMedia: config.socialMedia,
        openingHours: config.openingHours,
        cancellationPolicy: config.cancellationPolicy,
        requireUserApproval: config.requireUserApproval,
        updatedAt: new Date(),
      });
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loading size="lg" className="mx-auto mb-4" />
          <p className="text-gray-400">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  if (!businessId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">No hay negocio seleccionado</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Configuración</title>
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-amber-400">
              Configuración del Negocio
            </h1>
            <p className="text-gray-400 mt-2 text-sm sm:text-base">
              Personaliza cómo ven los usuarios tu negocio
            </p>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 flex-shrink-0 w-full sm:w-auto"
          >
            {saving ? (
              <>
                <Loading size="sm" className="mr-2" />
                <span className="hidden sm:inline">Guardando...</span>
                <span className="sm:hidden">Guardando</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Guardar Cambios</span>
                <span className="sm:hidden">Guardar</span>
              </>
            )}
          </Button>
        </div>

        {/* Sección de Suscripción */}
        <Card className="p-6 bg-gradient-to-br from-gray-900 via-gray-900 to-amber-950/20 border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Crown className="w-5 h-5 text-amber-400" />
            Plan y Suscripción
          </h2>

          {loadingSubscription ? (
            <div className="flex items-center justify-center py-8">
              <Loading size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Plan actual */}
              <div className="flex items-center gap-3 p-4 bg-gray-800/50 rounded-xl border border-gray-700">
                <div className={`p-3 rounded-xl ${subscription.plan === 'basic' ? 'bg-amber-500/20' :
                  subscription.plan === 'pro' ? 'bg-purple-500/20' : 'bg-gray-700'
                  }`}>
                  <Sparkles className={`w-6 h-6 ${subscription.plan === 'basic' ? 'text-amber-400' :
                    subscription.plan === 'pro' ? 'text-purple-400' : 'text-gray-400'
                    }`} />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-400">Tu plan actual</p>
                  <p className="text-xl font-bold text-white">
                    {systemConfig.plans[subscription.plan]?.name || 'Gratis'}
                  </p>
                  {subscription.currentPeriodEnd && (
                    <p className="text-xs text-gray-500">
                      {subscription.cancelAtPeriodEnd
                        ? `Se cancela el ${new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}`
                        : `Próxima renovación: ${new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()}`
                      }
                    </p>
                  )}
                </div>
                {subscription.plan !== 'free' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleManageSubscription}
                    disabled={processingCheckout}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Gestionar
                  </Button>
                )}
              </div>

              {/* Planes disponibles */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Plan Gratis */}
                <div className={`relative p-5 rounded-xl border-2 transition-all ${subscription.plan === 'free'
                  ? 'border-gray-500 bg-gray-800/50'
                  : 'border-gray-700 bg-gray-800/30 opacity-75'
                  }`}>
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-white">{systemConfig.plans.free.name}</h3>
                    <p className="text-3xl font-bold text-white mt-2">
                      0€ <span className="text-sm font-normal text-gray-400">/mes</span>
                    </p>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {systemConfig.plans.free.features.map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                        <Check className="w-4 h-4 text-gray-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {subscription.plan === 'free' && (
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 text-xs font-medium bg-gray-600 text-white rounded-full">
                        Actual
                      </span>
                    </div>
                  )}
                </div>

                {/* Plan Básico */}
                <div className={`relative p-5 rounded-xl border-2 transition-all ${subscription.plan === 'basic'
                  ? 'border-amber-500 bg-amber-950/30 ring-2 ring-amber-500/20'
                  : 'border-amber-500/50 bg-amber-950/10 hover:border-amber-500 hover:bg-amber-950/20'
                  }`}>
                  {subscription.plan !== 'basic' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 text-xs font-bold bg-amber-500 text-black rounded-full">
                        Recomendado
                      </span>
                    </div>
                  )}
                  {subscription.plan === 'basic' && (
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 text-xs font-medium bg-amber-500 text-black rounded-full">
                        Actual
                      </span>
                    </div>
                  )}
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-amber-400">{systemConfig.plans.basic.name}</h3>
                    <p className="text-3xl font-bold text-white mt-2">
                      {systemConfig.plans.basic.price}€ <span className="text-sm font-normal text-gray-400">/mes</span>
                    </p>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {systemConfig.plans.basic.features.map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                        <Check className="w-4 h-4 text-amber-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {subscription.plan === 'free' && (
                    <Button
                      onClick={() => handleSubscribe('basic')}
                      disabled={processingCheckout}
                      className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                    >
                      {processingCheckout ? (
                        <>
                          <Loading size="sm" className="mr-2" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <Crown className="w-4 h-4 mr-2" />
                          Contratar Plan
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Plan Pro */}
                <div className={`relative p-5 rounded-xl border-2 ${subscription.plan === 'pro'
                  ? 'border-purple-500 bg-purple-950/30'
                  : 'border-purple-500/30 bg-purple-950/10'
                  }`}>
                  {subscription.plan === 'basic' && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-3 py-1 text-xs font-bold bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full shadow-lg">
                        ⭐ RECOMENDADO
                      </span>
                    </div>
                  )}
                  {subscription.plan === 'pro' && (
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 text-xs font-medium bg-purple-500 text-white rounded-full">
                        Actual
                      </span>
                    </div>
                  )}
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-purple-400">{systemConfig.plans.pro.name}</h3>
                    <p className="text-3xl font-bold text-white mt-2">
                      {systemConfig.plans.pro.price}€ <span className="text-sm font-normal text-gray-400">/mes</span>
                    </p>
                  </div>
                  <ul className="space-y-2 mb-6">
                    {systemConfig.plans.pro.features.map((feature: string, idx: number) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                        <Check className="w-4 h-4 text-purple-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {(subscription.plan === 'free' || subscription.plan === 'basic') && (
                    <Button
                      onClick={() => handleSubscribe('pro')}
                      disabled={processingCheckout}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold"
                    >
                      {processingCheckout ? (
                        <>
                          <Loading size="sm" className="mr-2" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <Crown className="w-4 h-4 mr-2" />
                          {subscription.plan === 'basic' ? 'Mejorar Plan' : 'Contratar Plan'}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Nota informativa */}
              <div className="p-4 bg-blue-900/20 border border-blue-700/30 rounded-xl">
                <p className="text-sm text-blue-300">
                  <strong>💳 Pago seguro:</strong> Procesamos todos los pagos a través de Stripe.
                  Puedes cancelar tu suscripción en cualquier momento y seguirás teniendo acceso hasta el final del período pagado.
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Información básica */}
        <Card className="p-6 bg-gray-900 border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-400" />
            Información Básica
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nombre del Negocio *
              </label>
              <Input
                value={config.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Ej: Dojo Fitness Center"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Horario de Apertura
              </label>
              <Input
                value={config.openingHours}
                onChange={(e) => handleChange('openingHours', e.target.value)}
                placeholder="Ej: Lun-Vie 7:00-22:00, Sáb 9:00-14:00"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Descripción
              </label>
              <textarea
                value={config.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Describe tu negocio para que los usuarios sepan qué ofreces..."
                rows={3}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        </Card>

        {/* Imágenes */}
        <Card className="p-6 bg-gray-900 border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Image className="w-5 h-5 text-amber-400" />
            Imágenes
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Logo del Negocio
              </label>
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 rounded-xl overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Building2 className="w-10 h-10 text-gray-600" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    ref={logoInputRef}
                    onChange={(e) => handleImageUpload(e, 'logo')}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploadingLogo}
                    size="sm"
                  >
                    {uploadingLogo ? (
                      <>
                        <Loading size="sm" className="mr-2" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {logoPreview ? 'Cambiar' : 'Subir'}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">
                    Recomendado: 200x200px
                  </p>
                </div>
              </div>
            </div>

            {/* Cover Image */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Imagen de Portada
              </label>
              <div className="space-y-3">
                <div className="w-full h-32 rounded-xl overflow-hidden border border-gray-700 bg-gray-800 flex items-center justify-center">
                  {coverPreview ? (
                    <img
                      src={coverPreview}
                      alt="Cover"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Image className="w-10 h-10 text-gray-600" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    ref={coverInputRef}
                    onChange={(e) => handleImageUpload(e, 'cover')}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => coverInputRef.current?.click()}
                    disabled={uploadingCover}
                    size="sm"
                  >
                    {uploadingCover ? (
                      <>
                        <Loading size="sm" className="mr-2" />
                        Subiendo...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        {coverPreview ? 'Cambiar' : 'Subir'}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">
                    Recomendado: 1200x400px
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Política de Cancelación */}
        <Card className="p-6 bg-gray-900 border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-400" />
            Política de Cancelación
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Tiempo límite para cancelar sin penalización
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={config.cancellationPolicy.hoursBeforeClass}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    cancellationPolicy: {
                      ...prev.cancellationPolicy,
                      hoursBeforeClass: parseInt(e.target.value)
                    }
                  }))}
                  className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value={0}>Sin límite (siempre devolver crédito)</option>
                  <option value={1}>1 hora antes</option>
                  <option value={2}>2 horas antes</option>
                  <option value={3}>3 horas antes</option>
                  <option value={4}>4 horas antes</option>
                  <option value={6}>6 horas antes</option>
                  <option value={12}>12 horas antes</option>
                  <option value={24}>24 horas antes (1 día)</option>
                  <option value={48}>48 horas antes (2 días)</option>
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Si un usuario cancela después de este tiempo, perderá la clase de su bono igualmente.
              </p>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
              <p className="text-amber-400 text-sm">
                <strong>Ejemplo:</strong> Con {config.cancellationPolicy.hoursBeforeClass} horas de límite,
                si una clase es a las 18:00, el usuario debe cancelar antes de las {
                  config.cancellationPolicy.hoursBeforeClass === 0
                    ? '18:00 (cualquier momento)'
                    : `${18 - config.cancellationPolicy.hoursBeforeClass}:00`
                } para recuperar su crédito.
              </p>
            </div>

            {/* Límite de reservas por día */}
            <div className="pt-4 border-t border-gray-700">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Máximo de reservas por día por usuario
              </label>
              <div className="flex items-center gap-3">
                <select
                  value={config.cancellationPolicy.maxReservationsPerDay}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    cancellationPolicy: {
                      ...prev.cancellationPolicy,
                      maxReservationsPerDay: parseInt(e.target.value)
                    }
                  }))}
                  className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                >
                  <option value={1}>1 clase por día</option>
                  <option value={2}>2 clases por día</option>
                  <option value={3}>3 clases por día</option>
                  <option value={4}>4 clases por día</option>
                  <option value={5}>5 clases por día</option>
                  <option value={0}>Sin límite</option>
                </select>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Limita cuántas clases puede reservar un usuario en un mismo día.
              </p>
            </div>

            <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
              <p className="text-blue-400 text-sm">
                <strong>Configuración actual:</strong> Cada usuario puede reservar hasta{' '}
                <span className="font-bold">
                  {config.cancellationPolicy.maxReservationsPerDay === 0
                    ? 'clases ilimitadas'
                    : `${config.cancellationPolicy.maxReservationsPerDay} clase${config.cancellationPolicy.maxReservationsPerDay > 1 ? 's' : ''}`
                  }
                </span> por día.
              </p>
            </div>

            {/* Aprobación de usuarios */}
            <div className="pt-4 border-t border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <label className="block text-sm font-medium text-gray-300">
                    Aprobación manual de usuarios
                  </label>
                  <p className="text-gray-500 text-xs mt-1">
                    Si está activado, los nuevos usuarios necesitan tu aprobación para acceder al sistema.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setConfig(prev => ({ ...prev, requireUserApproval: !prev.requireUserApproval }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.requireUserApproval ? 'bg-amber-500' : 'bg-gray-600'
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.requireUserApproval ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
              </div>
              {config.requireUserApproval && (
                <div className="mt-3 p-3 bg-amber-900/20 border border-amber-700/50 rounded-lg">
                  <p className="text-amber-400 text-sm">
                    <strong>Activado:</strong> Los nuevos usuarios quedarán pendientes de aprobación hasta que los apruebes manualmente desde el Dashboard o la pantalla de Usuarios.
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Contacto */}
        <Card className="p-6 bg-gray-900 border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Phone className="w-5 h-5 text-amber-400" />
            Información de Contacto
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Teléfono
              </label>
              <Input
                value={config.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                placeholder="+34 600 000 000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email
              </label>
              <Input
                type="email"
                value={config.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="info@tunegocio.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <Globe className="w-4 h-4 inline mr-1" />
                Sitio Web
              </label>
              <Input
                value={config.website}
                onChange={(e) => handleChange('website', e.target.value)}
                placeholder="https://tunegocio.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Dirección
              </label>
              <Input
                value={config.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="Calle Principal 123, Ciudad"
              />
            </div>
          </div>
        </Card>

        {/* Redes Sociales */}
        <Card className="p-6 bg-gray-900 border-gray-800">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <Globe className="w-5 h-5 text-amber-400" />
            Redes Sociales
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Instagram
              </label>
              <Input
                value={config.socialMedia.instagram}
                onChange={(e) => handleChange('socialMedia.instagram', e.target.value)}
                placeholder="@tunegocio"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Facebook
              </label>
              <Input
                value={config.socialMedia.facebook}
                onChange={(e) => handleChange('socialMedia.facebook', e.target.value)}
                placeholder="facebook.com/tunegocio"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Twitter / X
              </label>
              <Input
                value={config.socialMedia.twitter}
                onChange={(e) => handleChange('socialMedia.twitter', e.target.value)}
                placeholder="@tunegocio"
              />
            </div>
          </div>
        </Card>

        {/* Botón guardar inferior */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            size="lg"
            className="bg-amber-600 hover:bg-amber-700"
          >
            {saving ? (
              <>
                <Loading size="sm" className="mr-2" />
                Guardando...
              </>
            ) : (
              <>
                <Save className="w-5 h-5 mr-2" />
                Guardar Todos los Cambios
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export default BusinessConfigPage;

